/**
 * @Description 行业商圈用的搜索框
 */
import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { AutoComplete, Input, Typography } from 'antd';
import { isArray, debounce } from '@lhb/func';
import { CloseCircleFilled } from '@ant-design/icons';
import { useMethods } from '@lhb/hook';
import { keywordPrompt } from '@/common/components/Map/V2AMap/utils/amap';
import { multidutySearch } from '@/common/api/siteselectionmap';
// import cs from 'classnames';
import styles from './index.module.less';
import IconFont from '@/common/components/IconFont';
import HighlightRow from '@/common/components/business/HighlightRow';
import V2Tag from '@/common/components/Data/V2Tag';
// import { getPoi } from '@/common/api/networkplan';
import useCurLocation from '@/common/components/Map/V2AMap/hooks/useCurLocation';
import { SEARCH_POI_ICON_SOLID_RED } from '@/common/utils/map';

const SearchInMap: FC<any> = ({
  searchConfig, // 自定义搜索框组件
  autoCompleteConfig, // 自定义自动完成组件
  mapIns, // 地图实例
  debounceTimeout = 300,
  finallyKeywords, // 将关键词暴露出去
  mapHelpfulInfo, // 地图辅助信息(注意：不一定是useAmapLevelAndCityNew的返回值)
  finallySearchResult, // 将搜索结果暴露出去
  finallySelected, // 将选中项暴露出去
  width = 180,
  historySearchList = [], // 历史搜索结果
  hideBorder = false, // 如果ui要求hover无边框，且不出现蓝色，传入该项为true(目前所有场景都用到了)
  panToLocation = true, // 定位成功后是否自动移动到响应位置
  zoomToAccuracy = true, // // 定位成功后是否自动调整级别
  externalKeywords = '', // 外部传入的keywords
  showSearchPOIMarker = true, // 是否显示搜索的POI的marker
  isSiteMapSearch = false, // 是否是选址地图的搜索模式（调用服务端接口，搜索结果包含门店、城市并且可选择）
}) => {
  const poiMarkerRef: any = useRef();
  const labelMarkerRef: any = useRef();
  const searchFnRef = useRef((str, targetCity) => loadData(str, targetCity));
  const overlayPOIGroups: any = useRef();
  const keywordsRef: any = useRef();
  // 注意，使用mapHelpfulInfo时一定要确认引用该组件的所有地方，目前mapHelpfulInfo不一定是useAmapLevelAndCityNew
  const { city } = mapHelpfulInfo;
  const [keywords, setKeywords] = useState<string>('');
  const [options, setOptions] = useState<any>(historySearchList);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const debounceSearch = useCallback(debounce((str) => searchFnRef.current(str, city), debounceTimeout), [city]);
  const locationInfo = useCurLocation(mapIns, {
    panToLocation: panToLocation, // 定位成功后是否自动移动到响应位置
    zoomToAccuracy: zoomToAccuracy, // 定位成功后是否自动调整级别
    showCircle: false,
  });
  const locationInfoRef: any = useRef(locationInfo);

  useEffect(() => {
    if (!mapIns) return;
    labelMarkerRef.current = new window.AMap.Marker({
      content: ' ',
      map: mapIns,
      anchor: 'top-left',
      offset: [-34, 6]
    });
  }, [mapIns]);

  useEffect(() => {
    locationInfoRef.current = locationInfo;
  }, [locationInfo]);


  // 搜索事件
  const searchHandle = (keywordStr: string) => {
    if (!keywordStr) {
      setOptions(historySearchList);
      finallySearchResult && finallySearchResult([]);
      finallySelected && finallySelected(null);
      return;
    };
    // 防止重新创建debounce函数
    debounceSearch(keywordStr);
  };

  useEffect(() => {
    keywordsRef.current = keywords;
    searchHandle(keywords);
  }, [keywords]);
  // 外部传入的keywords
  useEffect(() => {
    externalKeywords && setKeywords(externalKeywords);
  }, [externalKeywords]);

  // 如果在在搜索状态下，则先让options为空数组（接口数据返回太慢了）
  useEffect(() => {
    if (keywords && isLoading) {
      setOptions([]);
    }
  }, [isLoading, keywords]);

  const loadData = async (keywordStr: string, targetCity: any) => {
    if (isSiteMapSearch) {
      methods.loadMixedData(keywordStr, targetCity);
      return;
    }
    setIsLoading(true);
    // const searchResult = await getPoi({
    //   keyword: keywordStr,
    //   cityId: targetCity?.id,
    //   cityName: targetCity?.name
    // });
    const targetData = await keywordPrompt(keywordStr, {
      city: targetCity?.name || locationInfoRef.current?.cityName // 没有城市入参时就用用户当前定位的城市作为入参
    });
    // console.log(`searchResult`, targetData);
    setIsLoading(false);
    if (!isArray(targetData)) return;
    // 为了和getPoi接口里面的字段保持一致
    const searchResult = targetData.map((item: any) => {
      const { name, lng, lat } = item;
      return {
        ...item,
        poiName: name,
        longitude: lng,
        latitude: lat,
      };
    });
    finallySearchResult && finallySearchResult(searchResult);
    keywordsRef.current && setOptions(searchResult);
  };
  const createPoiMarker = (poi: any) => {
    const { poiName, latitude, longitude } = poi;
    const lnglat = [+longitude, +latitude];
    const marker = new window.AMap.Marker({
      map: mapIns,
      icon: new window.AMap.Icon({
        image: SEARCH_POI_ICON_SOLID_RED,
        size: [36, 36],
        imageSize: [36, 36],
        position: lnglat,
      }),
      anchor: 'bottom-center',
      position: lnglat,
    });
    marker.on('mouseover', () => {
      const labelContent = `<div class='amapPOISearchPoiMarkerLabel'>
      <div class='trangle'></div>
      <div class='marker'>${poiName}</div>
      </div>`;
      labelMarkerRef.current.setMap(mapIns); // 防止有的页面使用map.clear()
      labelMarkerRef.current.setPosition(lnglat);
      labelMarkerRef.current.setContent(labelContent);
    });
    marker.on('mouseout', () => {
      labelMarkerRef.current.setContent(' ');
    });
    return marker;
  };
  // 选择poi
  const selectHandle = (value: any, option: any, keywords) => {
    const { extraData } = option;
    option.keywords = keywords;
    finallySelected && finallySelected(option);
    const { poiName } = extraData;
    setKeywords(poiName);
    finallyKeywords && finallyKeywords(poiName);
    if (!showSearchPOIMarker) return;
    if (poiMarkerRef.current) {
      mapIns?.remove?.(poiMarkerRef.current);
    }
    const marker = createPoiMarker(extraData);
    mapIns?.setFitView?.(marker, true);
    poiMarkerRef.current = marker;
    // 每次先将新的覆盖物绘制在地图，然后再清空上一次遗留的覆盖物
    overlayPOIGroups.current && mapIns?.remove?.(overlayPOIGroups.current);
  };
  // 关键词变化
  const changeHandle = (str: string) => {
    setKeywords(str);
    finallyKeywords && finallyKeywords(str);
  };

  const searchIconClick = () => {
    if (!showSearchPOIMarker) return;
    if (!options?.length) return;
    if (!mapIns) return;
    const markers: any = [];
    options.forEach((optionItem: any) => {
      const marker = createPoiMarker(optionItem);
      markers.push(marker);
    });
    const overlayGroups = new window.AMap.OverlayGroup(markers);
    mapIns.add(overlayGroups);
    mapIns.setFitView(markers);
    // 每次先将新的覆盖物绘制在地图，然后再清空上一次遗留的覆盖物
    overlayPOIGroups.current && mapIns.remove(overlayPOIGroups.current);
    overlayPOIGroups.current = overlayGroups;
  };

  const clearHandle = () => {
    if (overlayPOIGroups.current) {
      mapIns.remove(overlayPOIGroups.current);
      overlayPOIGroups.current = null;
    }
    if (poiMarkerRef.current) {
      mapIns.remove(poiMarkerRef.current);
      poiMarkerRef.current = null;
    }
  };

  const methods = useMethods({
    loadMixedData: (str: string, city: any) => {
      setIsLoading(true);
      const params = {
        keyword: str,
        cityId: city?.id,
        page: 1,
        size: 50,
      };
      multidutySearch(params).then((res: any) => {
        const searchResult = res?.filter((item) => item?.location)?.map((item: any) => {
          const { location } = item;
          const lnglat = location?.split(',');
          const lng = lnglat[0];
          const lat = lnglat[1];
          return {
            ...item,
            lng,
            lat,
            longitude: lng,
            latitude: lat,
          };
        });
        finallySearchResult && finallySearchResult(searchResult);
        keywordsRef.current && setOptions(searchResult);
      }).finally(() => {
        setIsLoading(false);
      });
    }
  });

  return (
    <AutoComplete
      value={keywords}
      options={options.map((item: any, index) => {
        return {
          // 原本value:index(控制台会报错，value不应该为number类型)
          value: index + item?.poiName,
          name: item?.poiName,
          extraData: item,
          label: (
            keywords
            // 搜索结果
              ? <div className='mb-7'>
                <div className={isSiteMapSearch ? styles.flexCon : ''}>
                  {
                    isSiteMapSearch && item.type === 1 ? <V2Tag color='blue'>门店</V2Tag> : <></>
                  }
                  <HighlightRow
                    className='fn-14 c-222'
                    text={item.poiName}
                    keywords={keywords}
                  />
                </div>

                <Typography.Text ellipsis={{ tooltip: item.address }} style={{
                  color: '#959bab' // 超出时的省略号保持和文字颜色一致
                }}>
                  <span className='fn-12 c-959'>
                    {item.address}
                  </span>
                </Typography.Text>
              </div>

            // 历史搜索记录
              : <Typography.Text ellipsis={{ tooltip: item.poiName }}>
                <div className='c-999 fs-12'>
                  <IconFont iconHref='iconlishi' className='mr-8'/>

                  {item.poiName}
                </div>
              </Typography.Text>
          )
        };
      })}
      onSearch={searchHandle}
      onSelect={(value, option) => selectHandle(value, option, keywords)}
      onChange={changeHandle}
      style={{
        marginRight: '8px'
      }}
      className={hideBorder ? styles.hideBorder : ''}
      {
        ...autoCompleteConfig
      }
    >
      <Input
        placeholder='请输入具体地址'
        allowClear={{
          clearIcon: <CloseCircleFilled onClick={clearHandle}/>
        }}
        suffix={<IconFont
          iconHref='iconsearch'
          className='c-959'
          onClick={searchIconClick}
        />
        }
        style={{ width }}
        {
          ...searchConfig
        }
      />
    </AutoComplete>
  );
};

export default SearchInMap;
